import { type R2SPlacement } from '@kijiji/generated/graphql-types'
import { useTranslation } from 'next-i18next'
import { type FC } from 'react'

import { VipStickyBottomSection } from '@/components/vip/vip-sticky-components/styled'
import { TRANSLATION_KEYS } from '@/constants/localization'
import {
  RequestViewing,
  RequestViewingType,
} from '@/features/listing/components/request-viewing/RequestViewing'
import { useReplyToSellerModal } from '@/features/listing/hooks/reply-to-seller/useReplyToSellerModal'
import { getPriceClassification } from '@/features/listing/utils/getPriceClassification'
import { isDealerListing } from '@/features/listing/utils/isDealerListing'
import { isListingEligibleForRequestViewing } from '@/features/listing/utils/request-viewing/isListingEligibleForRequestViewing'
import { ProfilePhoneNumberInline } from '@/features/profile/components/phone-number/ProfilePhoneNumberInline'
import { type VipQueryListing } from '@/types/listings'
import { Button } from '@/ui/atoms/button'
import { generateVIPPhoneTrackingLabel } from '@/utils/vip/generateVIPPhoneTrackingLabel'

export type VipStickyBottomProps = {
  listing: VipQueryListing
  r2sPlacement?: R2SPlacement
  isGSITrackingEnabled?: boolean
}

export const VipStickyBottom: FC<VipStickyBottomProps> = ({
  listing,
  r2sPlacement,
  isGSITrackingEnabled,
}) => {
  const { t } = useTranslation([TRANSLATION_KEYS.R2S, TRANSLATION_KEYS.REQUEST_VIEWING])

  const getPhoneTrackingLabel = () => {
    if (!listing.posterInfo.phoneNumber) return ''
    const priceRating = getPriceClassification(listing.price)?.rating

    return generateVIPPhoneTrackingLabel({
      isDealerListing: isDealerListing(listing.attributes),
      eventLocation: 'top',
      priceInsightRate: priceRating,
      categoryId: listing.categoryId,
      placement: r2sPlacement,
    })
  }

  const phoneProps = {
    categoryId: listing.categoryId,
    profileId: listing.posterInfo.posterId,
    sellerType: listing.posterInfo.sellerType,
    isGSITrackingEnabled: isGSITrackingEnabled,
    listingId: listing.id,
    trackingLabel: getPhoneTrackingLabel(),
  }

  const { handleOpenModal } = useReplyToSellerModal()
  const handleOpenR2SForm = () => {
    handleOpenModal(listing, r2sPlacement)
  }

  const isRequestViewingButtonVisible = isListingEligibleForRequestViewing(listing)

  return (
    <VipStickyBottomSection data-testid="vip-sticky-bottom">
      {listing.posterInfo.phoneNumber && !isRequestViewingButtonVisible ? (
        <ProfilePhoneNumberInline variant="button" {...phoneProps} />
      ) : null}

      <Button
        variant={isRequestViewingButtonVisible ? 'secondary' : 'primary'}
        isFullWidth
        onClick={handleOpenR2SForm}
      >
        {t('r2s:cta.send_message')}
      </Button>

      <RequestViewing source="VIP" listing={listing} type={RequestViewingType.DEFAULT} />
    </VipStickyBottomSection>
  )
}
